import { useMemo } from "react"

import { useQuery } from "@tanstack/react-query"
import { TConversation, TMessage } from "types/transcript"

import { avatarUserForConversation } from "src/components/ChatUser"
import { useCurrentMsk, useESKAvailable } from "src/queries/useConfig"
import { useCurrentConversation, usePastConversations } from "src/queries/useConversation"
import { useToDoList } from "src/queries/useToDoList"
import { useChatMessageList } from "./useChatMessageList"

export interface TChatSection {
  conversationId: string
  sidekickName: string
  avatarId: number
  mskAvatarId?: number
  isCurrent: boolean
  topic: string
  date: string
  contentModuleName?: string
  data: TMessage[]
}

export const useGroupedChatMessages = () => {
  const { messages, isLoading, isPending } = useChatMessageList()
  const { pastConversations } = usePastConversations()
  const { data: currentConversation } = useCurrentConversation()
  const currentMsk = useCurrentMsk()
  const { data: eskData } = useESKAvailable()
  const { data: todoList } = useToDoList()

  // Extended conversations loaded on-demand (same cache key as useChatMessageList).
  const { data: extendedPastConversations = [] } = useQuery<TConversation[]>({
    queryKey: ["conversations", "extended-past"],
    queryFn: () => [],
    staleTime: Infinity,
    gcTime: Infinity,
  })

  const groupedMessages = useMemo(() => {
    // Map to store conversation metadata for quick lookup.
    // Defensive null-filter: pastConversations is sourced from MMKV/localStorage
    // and can contain null entries from older buggy writes or cross-tab races
    // (other call sites in useConversation.ts use `c?.id` for the same reason).
    const conversationMap = new Map<string, TConversation>()
    pastConversations.forEach((c) => {
      if (c?.id) conversationMap.set(c.id, c)
    })
    // Include on-demand loaded older conversations in the metadata map so
    // ConversationHeader renders the correct sidekick name/avatar for them.
    extendedPastConversations.forEach((c) => {
      if (c?.id) conversationMap.set(c.id, c)
    })
    if (currentConversation) conversationMap.set(currentConversation.id, currentConversation)

    // Build a map of task_id to content module name for quick lookup
    const contentModuleMap = new Map<string, string>()
    if (todoList) {
      todoList.forEach((contentModule) => {
        if (contentModule.task_id && contentModule.name) {
          contentModuleMap.set(contentModule.task_id, contentModule.name)
        }
      })
    }

    // Build a map of ESK type (UUID) to topic displayName for quick lookup
    // Uses cm_topics where sk_id matches the ESK's type UUID
    const topicMap = new Map<string, string>()
    if (eskData) {
      eskData.forEach((sponsor) => {
        if (sponsor.cm_topics) {
          sponsor.cm_topics.forEach((topic) => {
            if (topic.sk_id && topic.displayName) {
              topicMap.set(topic.sk_id, topic.displayName)
            }
          })
        }
      })
    }

    const sections: TChatSection[] = []

    // Get current MSK avatar ID
    const currentMskAvatarId = currentMsk ? parseInt(currentMsk.avatar_id, 10) : undefined

    // Helper to get content module name from conversation
    const getContentModuleName = (convo: TConversation | undefined): string | undefined => {
      if (convo?.active_task_id) {
        return contentModuleMap.get(convo.active_task_id)
      }
      return undefined
    }

    // Helper to determine the best topic for a conversation
    // Priority: conversation.topic > cm_topics displayName > "General"
    const getConversationTopic = (convo: TConversation | undefined): string => {
      if (convo?.topic && convo.topic.trim() !== "") {
        return convo.topic
      }
      if (convo) {
        const topicName = topicMap.get(convo.active_esk_type?.toString())
        if (topicName && topicName.trim() !== "") {
          return topicName
        }
      }
      return "General"
    }

    // Pre-populate with current conversation if it exists
    if (currentConversation) {
      const avatarInfo = avatarUserForConversation(currentConversation, currentMsk)
      const topic = getConversationTopic(currentConversation)
      const contentModuleName = getContentModuleName(currentConversation)

      sections.push({
        conversationId: currentConversation.id,
        sidekickName: avatarInfo.name || "Sidekick",
        avatarId: avatarInfo.avatarId,
        mskAvatarId: !avatarInfo.isMsk ? currentMskAvatarId : undefined,
        isCurrent: true,
        topic,
        contentModuleName,
        date: currentConversation.started_at || new Date().toISOString(),
        data: [],
      })
    }

    // For an inverted list, we keep messages in descending order (newest first)
    // and reverse sections at the end so newest conversation appears at the "top" (bottom visually)
    // Within each section, messages are also newest-first
    const descendingMessages = messages // Already newest first

    let currentSection: TChatSection | null = sections[0] || null

    descendingMessages.forEach((message) => {
      const convoId = message.conversation_id || "unknown"

      if (!currentSection || currentSection.conversationId !== convoId) {
        // Check if we already have a section for this conversation (e.g. currentConversation)
        const existingSection = sections.find((s) => s.conversationId === convoId)

        if (existingSection) {
          currentSection = existingSection
        } else {
          const convo = conversationMap.get(convoId)
          const avatarInfo = avatarUserForConversation(convo, currentMsk)
          const topic = getConversationTopic(convo)
          const contentModuleName = getContentModuleName(convo)

          currentSection = {
            conversationId: convoId,
            sidekickName: avatarInfo.name || message.sidekick_name || "Sidekick",
            avatarId: avatarInfo.avatarId,
            mskAvatarId: !avatarInfo.isMsk ? currentMskAvatarId : undefined,
            isCurrent: currentConversation?.id === convoId,
            topic,
            contentModuleName,
            date: convo?.started_at || message.created_at || new Date().toISOString(),
            data: [],
          }
          sections.push(currentSection)
        }
      }

      currentSection.data.push(message)
    })

    // For inverted list: keep as-is since messages are already newest-first
    // sections[0] = newest conversation, sections[0].data[0] = newest message
    // With inverted={true}, index 0 appears at the bottom of the screen
    return sections
  }, [
    messages,
    pastConversations,
    extendedPastConversations,
    currentConversation,
    currentMsk,
    eskData,
    todoList,
  ])

  return {
    sections: groupedMessages,
    isLoading,
    isPending,
  }
}
